import { h } from '@omni-torn/ui';
import { api } from './api';
import { setRenderer, navigateTo } from './app/nav';
import { renderAccount } from './app/pages/account';
import { registerServiceWorker, renderAlerts, startSiteAlerts } from './app/pages/alerts';
import { renderAttacks } from './app/pages/attacks';
import { renderBattle } from './app/pages/battle';
import { renderCompany } from './app/pages/company';
import { renderFaction } from './app/pages/faction';
import { renderLogs } from './app/pages/logs';
import { renderMyCrimes } from './app/pages/mycrimes';
import { renderEnergy } from './app/pages/energy';
import { renderTrading } from './app/pages/trading';
import { renderEvents } from './app/pages/events';
import { renderRacing } from './app/pages/racing';
import { renderItemStats } from './app/pages/item-stats';
import { renderLookup, setDataMode } from './app/pages/lookup';
import { renderLoadouts } from './app/pages/loadouts';
import { renderLeaderboards } from './app/pages/leaderboards';
import { renderLoot } from './app/pages/loot';
import { renderMarket } from './app/pages/market';
import { renderPricelists } from './app/pages/pricelists';
import { renderOverview } from './app/pages/overview';
import { renderOwner } from './app/pages/owner';
import { renderBotStats } from './app/pages/bot-stats';
import { renderSignIn } from './app/pages/signin';
import { renderSpies } from './app/pages/spies';
import { renderStats } from './app/pages/stats';
import { renderStocks } from './app/pages/stocks';
import { renderTargets } from './app/pages/targets';
import { renderTerms } from './app/pages/terms';
import { renderBot } from './app/pages/bot';
import { renderTravel } from './app/pages/travel';
import { renderBank, renderCrimes, renderHof, renderHonors, renderItems, renderTerritory } from './app/pages/torn';
import { renderFactions } from './app/pages/factions';
import { renderRankedWars } from './app/pages/ranked-wars';
import { renderElo, renderMerits, renderRespect } from './app/pages/calc';
import { renderCalc } from './app/pages/calc-tools';
import { handOverKey, loadMe, markTrackingSeen, restore, session, trackingSeen } from './app/session';
import { savePrefs } from './app/pages/shared';
import { termsBanner, termsGate } from './app/terms-gate';
import { drawShell, showSection, type Page } from './app/shell';
import { startSync, stopSync } from './app/sync';
import { problem } from './app/ui';
import { renderPanel } from './panel';
import { applyTheme, savedTheme, THEME_KEY, type ThemeChoice } from './theme-core';

/**
 * The Omni-Torn website. Nothing but the sign-in page until you sign in with a Torn key (or
 * through the extension); then a side menu of sections, each opened or locked by what your key
 * can read and your Omni-Torn level. /panel (the Discord bot's control panel) has its own sign-in; /terms (Torn's
 * terms table, whole) and /bot (the Discord bot: invite links, its server terms) need none.
 */

const view = document.getElementById('view') as HTMLElement;

// ---------------------------------------------------------------- light / dark

const themeButton = document.getElementById('theme-toggle') as HTMLButtonElement | null;
const THEME_LABEL: Record<ThemeChoice, string> = { auto: 'Auto', light: 'Light', dark: 'Dark' };
const showTheme = (c: ThemeChoice) => {
  if (!themeButton) return;
  themeButton.textContent = THEME_LABEL[c];
  themeButton.setAttribute('aria-label', `Theme: ${THEME_LABEL[c]} (click to change)`);
};
let themeNow = savedTheme();
showTheme(themeNow);
themeButton?.addEventListener('click', () => {
  // Auto → Light → Dark → Auto.
  const next: ThemeChoice = ({ auto: 'light', light: 'dark', dark: 'auto' } as const)[themeNow];
  themeNow = next;
  try {
    if (next === 'auto') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, next);
  } catch {
    /* no storage (private window): just this page */
  }
  applyTheme(next);
  showTheme(next);
});

// ---------------------------------------------------------------- pages

const PAGES: Record<string, Page> = {
  overview: renderOverview,
  stats: renderStats,
  battle: renderBattle,
  attacks: renderAttacks,
  logs: renderLogs,
  mycrimes: renderMyCrimes,
  energy: renderEnergy,
  events: renderEvents,
  racing: renderRacing,
  itemstats: renderItemStats,
  trading: renderTrading,
  company: renderCompany,
  faction: renderFaction,
  travel: renderTravel,
  market: renderMarket,
  pricelist: renderPricelists,
  stocks: renderStocks,
  lookup: renderLookup,
  loadouts: renderLoadouts,
  items: renderItems,
  bank: renderBank,
  honors: renderHonors,
  hof: renderHof,
  factions: renderFactions,
  rankedwars: renderRankedWars,
  territory: renderTerritory,
  crimes: renderCrimes,
  merits: renderMerits,
  respect: renderRespect,
  elo: renderElo,
  spies: renderSpies,
  loot: renderLoot,
  targets: renderTargets,
  leaderboards: renderLeaderboards,
  calc: renderCalc,
  alerts: renderAlerts,
  account: renderAccount,
  owner: renderOwner,
  'bot-stats': renderBotStats,
};

// The installable site (S34): the service worker keeps only the site's shell; your alerts as notifications while the
// site is open, if you allowed it on the Alerts page.
registerServiceWorker();
startSiteAlerts();

let restored: Promise<boolean> | null = null;
let signInNote = '';
let drawing = 0;

async function render(): Promise<void> {
  const mine = ++drawing;
  // Old links: /player/123 is /lookup/123 now.
  const old = /^\/player\/(\d+)\/?$/.exec(location.pathname);
  if (old) history.replaceState(null, '', `/lookup/${old[1]}`);
  if (/^\/panel\/?$/.test(location.pathname)) {
    drawShell();
    document.getElementById('sidenav')!.hidden = true;
    await renderPanel(view);
    return;
  }
  // Torn's terms table, whole — for everyone (the Discord bot links here), signed in or not.
  if (/^\/terms\/?$/.test(location.pathname)) {
    drawShell();
    document.getElementById('sidenav')!.hidden = true;
    renderTerms(view);
    return;
  }
  // The Discord bot: what it does, Add to Discord, the bot's server terms — for everyone, signed in or not.
  if (/^\/bot\/?$/.test(location.pathname)) {
    drawShell();
    document.getElementById('sidenav')!.hidden = true;
    renderBot(view);
    return;
  }
  restored ??= restore();
  const ok = (await restored) && !!session.get();
  if (mine !== drawing) return;
  if (!ok) {
    drawShell();
    const note = signInNote;
    signInNote = '';
    await renderSignIn(view, () => {
      restored = Promise.resolve(true);
      void afterSignIn();
    }, note);
    return;
  }
  if (!session.me()) {
    try {
      await loadMe();
    } catch (err) {
      if (!session.get()) return; // signed out on the way (render runs again)
      view.replaceChildren(problem(err));
      return;
    }
  }
  if (mine !== drawing) return;
  drawShell();
  // Never agreed to any version of the terms: Torn's table with Agree / Sign out instead of every page, nothing else
  // running (app/terms-gate.ts). Agreeing draws the page.
  if (termsGate(view, () => void render())) {
    document.getElementById('sidenav')!.hidden = true;
    notices.replaceChildren();
    return;
  }
  startSync();
  // Background tracking needs your key on the server once (signed in before it kept keys): hand it
  // over — after saying so once, if this browser hasn't shown it yet.
  void handOverKey().then((ok) => (ok ? loadMe() : null)).catch(() => {});
  trackingNotice();
  termsBanner();
  await showSection(view, PAGES);
  view.focus({ preventScroll: true });
}

const notices = document.getElementById('notices') as HTMLElement;

/** Signed in before background tracking: said once here, with OK (hand the key over) or Switch it off. */
function trackingNotice(): void {
  const m = session.me();
  notices.replaceChildren();
  if (!m || trackingSeen() || !m.tracking.enabled || !m.prefs.track || (m.tracking.tracked && m.tracking.status === 'ok')) return;
  const status = h('span', { class: 'save-status', role: 'status' });
  const answer = async (on: boolean) => {
    markTrackingSeen();
    status.textContent = 'Saving…';
    try {
      // (No "switch it off" any more — one agreement: removing the key everywhere, or deleting your data, stops it.)
      if (on) await handOverKey(true);
      await loadMe();
      notices.replaceChildren();
    } catch (err) {
      status.textContent = (err as Error).message;
    }
  };
  notices.append(
    h(
      'div',
      { class: 'notice ask', 'data-notice': 'tracking' },
      h('strong', {}, 'New: background tracking. '),
      'Omni-Torn can keep your history, fights and log up to date even when this site and the extension are closed. For that its server keeps the key you sign in with — encrypted — and uses it only to read your own data. To stop, remove your key everywhere (Account) or delete your data; the key is then deleted.',
      h('div', { class: 'ask-buttons' }, h('button', { type: 'button', class: 'btn-primary', onclick: () => void answer(true) }, 'OK, track me'), status),
    ),
  );
}

async function afterSignIn(): Promise<void> {
  try {
    await loadMe();
  } catch {
    /* render shows it */
  }
  // The address as it was, query too (a shared Loadouts link keeps its snapshot through the sign-in).
  navigateTo(location.pathname === '/' ? '/' : location.pathname + location.search);
}

session.onSignedOut((why) => {
  stopSync();
  notices.replaceChildren();
  document.getElementById('terms-banner')?.remove();
  restored = Promise.resolve(false);
  signInNote = why;
  if (!/^\/panel/.test(location.pathname)) void render();
});

setRenderer(() => void render());
window.addEventListener('popstate', () => void render());
document.addEventListener('click', (e) => {
  const link = (e.target as Element).closest('a[data-link]');
  if (!link || e.defaultPrevented || (e as MouseEvent).ctrlKey || (e as MouseEvent).metaKey || (e as MouseEvent).shiftKey) return;
  e.preventDefault();
  navigateTo(link.getAttribute('href') ?? '/');
});

// ---------------------------------------------------------------- data mode

async function loadHealth(): Promise<void> {
  try {
    const health = await api.health();
    setDataMode(health.dataMode);
    session.setTornBase(health.tornApi);
    if (health.dataMode === 'mock') {
      document.body.prepend(
        h('div', { class: 'mock-banner', role: 'status', 'data-testid': 'mock-banner' }, h('strong', {}, 'Offline mock mode. '), 'Everything shown here is invented test data from a fake Torn API, not real Torn players.'),
      );
    }
  } catch {
    setDataMode('unknown');
  }
}

await loadHealth();
void render();
